/**
 * Services Plugin for RiotJS Components
 *
 * Automatically injects common services into components based on flags:
 * - locale: Always available (no opt-out)
 * - tools: Available by default (opt-out with `includeTools: false`)
 * - store: Opt-in with `includeStore: true`
 *
 * @example
 * // Most components (locale always included, tools by default)
 * export default {
 *     async onMounted() {
 *         const user = await this.tools.getUserContext();
 *     }
 * }
 *
 * @example
 * // Component using translations
 * <button>{locale.t('save')}</button>
 * <span>{locale.t('item_count', { count: items.length })}</span>
 *
 * @example
 * // Pure presentation component (opt-out of tools only)
 * export default {
 *     includeTools: false,
 *     onMounted() {
 *         this.root.innerHTML = this.props.html;
 *     }
 * }
 *
 * @example
 * // Component needing store (opt-in)
 * export default {
 *     includeStore: true,
 *     async onMounted() {
 *         this.unsubscribe = this.store.subscribe(() => {
 *             this.state.data = this.store.getState().data;
 *             this.update();
 *         });
 *     },
 *     onBeforeUnmount() {
 *         if (this.unsubscribe) {
 *             this.unsubscribe();
 *         }
 *     }
 * }
 */
import { getTools, getStore } from '../services/service-loader.js';
import { localeService } from '../services/localeService.js';

export function createServicesPlugin() {
    return function servicesPlugin(component) {
        // Check component flags
        // locale: always included (no opt-out)
        // tools: default true (opt-OUT pattern - most components need it)
        // store: default false (opt-IN pattern - fewer components need it)
        const includeTools = component.includeTools !== false;
        const includeStore = component.includeStore === true;

        // Instance variables to hold loaded services
        let toolsInstance = null;
        let storeInstance = null;

        // Add locale getter (always available - static import, no async needed)
        Object.defineProperty(component, 'locale', {
            get() {
                return localeService;
            },
            configurable: true,
            enumerable: true
        });

        // Add tools getter if requested
        if (includeTools) {
            Object.defineProperty(component, 'tools', {
                get() {
                    return toolsInstance;
                },
                configurable: true,
                enumerable: true
            });
        }

        // Add store getter if requested
        if (includeStore) {
            Object.defineProperty(component, 'store', {
                get() {
                    return storeInstance;
                },
                configurable: true,
                enumerable: true
            });
        }

        // Only wrap onMounted if async services (tools or store) are needed
        // Note: locale is always available synchronously, no async loading needed
        if (includeTools || includeStore) {
            const originalOnMounted = component.onMounted;
            component.onMounted = async function() {
                // Load requested services (cached by service-loader)
                if (includeTools) {
                    toolsInstance = await getTools();
                }

                if (includeStore) {
                    storeInstance = await getStore();
                }

                // Call original onMounted if it exists
                if (originalOnMounted) {
                    const result = originalOnMounted.call(this);
                    // Handle both sync and async onMounted
                    if (result instanceof Promise) {
                        await result;
                    }
                }

                // Trigger update to make services available in template
                // This ensures tools?.method() and store calls work after initial render
                this.update();
            };
        }

        return component;
    };
}

export const servicesPlugin = createServicesPlugin();
